import { useGLTF } from '../../hooks';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { ThreeElements } from '@react-three/fiber';
import { getHoleNames } from '@slope/three';

export type VirtualCourseProps = {
  src: string;
  hole?: 'all' | (string & {});
  clone?: boolean;
  onLoad?: (root: THREE.Object3D, holeNames: string[]) => void;
  // Exclude certain holes from being rendered
  exclude?: (hole: THREE.Object3D) => boolean;
} & ThreeElements['group'];

export const VirtualCourse: React.FC<VirtualCourseProps> = ({
  src,
  hole = 'all',
  onLoad = () => {},
  exclude,
  castShadow = true,
  receiveShadow = true,
  ...restProps
}) => {
  const { scene } = useGLTF(src);

  const holeNames = useMemo(() => getHoleNames(scene), [scene]);

  const cloned = useMemo(() => scene.clone(), [scene]);

  const holesMap = useMemo(() => {
    const map: Record<string, THREE.Object3D> = {};
    cloned.traverse((child) => {
      if (holeNames.has(child.name)) {
        map[child.name] = child;
      }
      if (child.name === 'course') {
        map['all'] = child;
      }
    });
    return map;
  }, [holeNames, cloned]);

  useEffect(() => {
    Object.entries(holesMap).forEach(([name, obj]) => {
      obj.visible = name === hole;
    });
    onLoad(holesMap[hole], Array.from(holeNames));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hole, holeNames, holesMap]);

  useEffect(() => {
    return () => {
      cloned.traverse((child) => {
        child.castShadow = castShadow ?? false;
        child.receiveShadow = receiveShadow ?? false;
        if (exclude?.(child)) {
          child.visible = false;
        }
      });
    };
  }, [cloned, exclude]);

  return <primitive object={cloned} dispose={null} {...restProps} />;
};
